var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix, MatrixUtil } from '@printu/geom';
import { hasCreateImageBitmap, hasCreateImageResizeBug, } from 'app/utils/caniuse';
import { canvasSetSmoothing } from 'app/utils/canvas';
import { getEdgeTransparencyInsets } from '../rasterize/trimTransparency';
const isRunningInWorker = () => {
    // run this in global scope of window or worker. since window.self = window, we're ok
    if (typeof WorkerGlobalScope !== 'undefined' &&
        self instanceof WorkerGlobalScope) {
        return true;
    }
    return false;
};
export const DefaultOpts = {
    downscale: 0.1,
    prescale: 0.5,
    prescaleAreaThreshold: 10000000,
    defaultBlurRadius: 16,
};
export function getPixelateSizes(imageSize, orientation, opts) {
    const $opts = Object.assign(Object.assign({}, DefaultOpts), opts);
    const prescale = imageSize.width * imageSize.height > $opts.prescaleAreaThreshold
        ? $opts.prescale
        : 1;
    const prescaledImageWidth = Math.round(prescale * imageSize.width);
    const prescaledImageHeight = Math.round(prescale * imageSize.height);
    let pixelateWidth = Math.round($opts.downscale * prescaledImageWidth);
    let pixelateHeight = Math.round($opts.downscale * prescaledImageHeight);
    let cyclesGuard = 10;
    while (cyclesGuard > 0 && (pixelateWidth > 200 || pixelateHeight > 200)) {
        cyclesGuard -= 1;
        const stepDownscale = 1 - $opts.downscale;
        pixelateWidth = Math.round(stepDownscale * pixelateWidth);
        pixelateHeight = Math.round(stepDownscale * pixelateHeight);
    }
    let reorientImageWidth = prescaledImageWidth;
    let reorientImageHeight = prescaledImageHeight;
    let reorientMatrix = new Matrix();
    if (orientation !== 1 /* ImageOrientation.up */) {
        const { transform, size } = getOrientationTransformAndBounds(orientation, imageSize);
        reorientMatrix = transform;
        reorientImageWidth = size.width;
        reorientImageHeight = size.height;
    }
    return {
        imageWidth: imageSize.width,
        imageHeight: imageSize.height,
        pixelateWidth,
        pixelateHeight,
        reorientMatrix,
        reorientImageWidth,
        reorientImageHeight,
    };
}
export function getOrientationTransform(orient) {
    const matrix = new Matrix();
    switch (orient) {
        case 1 /* ImageOrientation.up */: // EXIF 1
            break;
        case 3 /* ImageOrientation.down */: // EXIF 3
            matrix.rotate(180);
            break;
        case 6 /* ImageOrientation.left */: // EXIF 6
            matrix.rotate(90);
            break;
        case 8 /* ImageOrientation.right */: // EXIF 8
            matrix.rotate(270);
            break;
        case 2 /* ImageOrientation.upMirrored */: // EXIF 2
            matrix.scale(-1, 1);
            break;
        case 4 /* ImageOrientation.downMirrored */: // EXIF 4
            matrix.scale(1, -1);
            break;
        case 5 /* ImageOrientation.leftMirrored */: // EXIF 5
            matrix.scale(-1, 1).rotate(270);
            break;
        case 7 /* ImageOrientation.rightMirrored */: // EXIF 7
            matrix.scale(-1, 1).rotate(90);
            break;
        default:
            break;
    }
    return matrix;
}
export function getOrientationTransformAndBounds(orient, imgSize) {
    const matrix = getOrientationTransform(orient);
    const [tWidth, tHeight] = MatrixUtil.transformPoint(imgSize.width, imgSize.height, matrix);
    return {
        transform: matrix,
        size: { width: Math.abs(tWidth), height: Math.abs(tHeight) },
    };
}
const disposeImage = (image) => {
    image.onerror = null;
    image.onload = null;
    image.src = '';
    try {
        delete image.src;
    }
    catch (error) {
        /** noop */
    }
};
const loadImage = (source) => __awaiter(void 0, void 0, void 0, function* () {
    if (!isRunningInWorker()) {
        const sourceUrl = URL.createObjectURL(source);
        let image;
        try {
            image = yield new Promise((resolve, reject) => {
                const img = new Image();
                img.onerror = reject;
                img.onload = () => resolve(img);
                img.src = sourceUrl;
            });
        }
        catch (err) {
            /** swallow */
        }
        URL.revokeObjectURL(sourceUrl);
        return {
            image,
            dispose: () => {
                disposeImage(image);
            },
        };
    }
    const image = yield createImageBitmap(source);
    return { image, dispose: () => image === null || image === void 0 ? void 0 : image.close() };
});
export function downscaleImage(source, size, canvasProvider, opts) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const { isSafari = false } = opts !== null && opts !== void 0 ? opts : {};
        const { width: pixelateWidth, height: pixelateHeight } = size;
        let insets;
        let originalImage;
        let disposeDownscaledImage;
        let downscaledImage;
        if (hasCreateImageBitmap() && !isSafari) {
            const hasImageResizeBug = yield hasCreateImageResizeBug();
            if (!hasImageResizeBug) {
                try {
                    const downscaledBitmap = yield createImageBitmap(source, {
                        resizeWidth: pixelateWidth,
                        resizeHeight: pixelateHeight,
                        resizeQuality: 'pixelated',
                    });
                    disposeDownscaledImage = () => downscaledBitmap.close();
                    downscaledImage = downscaledBitmap;
                }
                catch (error) {
                    downscaledImage = undefined;
                    disposeDownscaledImage = undefined;
                }
            }
        }
        if (!downscaledImage) {
            try {
                originalImage = yield loadImage(source);
                const downscaledCanvas = canvasProvider.createCanvas(pixelateWidth, pixelateHeight);
                const ctx = downscaledCanvas.getContext('2d', {
                    alpha: false,
                });
                canvasSetSmoothing(ctx, false);
                ctx.drawImage(originalImage.image, 0, 0, originalImage.image.width, originalImage.image.height, 0, 0, pixelateWidth, pixelateHeight);
                canvasSetSmoothing(ctx, true);
                disposeDownscaledImage = () => {
                    canvasProvider.disposeCanvas(downscaledCanvas);
                };
                downscaledImage = downscaledCanvas;
            }
            catch (error) {
                downscaledImage = undefined;
                disposeDownscaledImage = undefined;
            }
        }
        // NOTE: we only support PNG and JPEG images,
        const hasTransparency = source.type === 'image/png';
        if (hasTransparency) {
            originalImage = originalImage !== null && originalImage !== void 0 ? originalImage : (yield loadImage(source));
            const imageCanvas = canvasProvider.createCanvas(originalImage.image.width, originalImage.image.height);
            const ctx = imageCanvas.getContext('2d');
            ctx.drawImage(originalImage.image, 0, 0);
            insets = getEdgeTransparencyInsets(imageCanvas);
            console.log(insets);
            canvasProvider.disposeCanvas(imageCanvas);
        }
        if (originalImage) {
            try {
                (_a = originalImage.dispose) === null || _a === void 0 ? void 0 : _a.call(originalImage);
            }
            catch (_) {
                /** swallow */
            }
        }
        return {
            image: downscaledImage,
            insets,
            dispose: disposeDownscaledImage,
        };
    });
}
